/**
 * Хелпери для колонок `timestamp without time zone`. Їх у базі 133 зі 133, тобто це не окремий
 * випадок, а єдиний спосіб, яким на цьому API живуть позначки часу.
 *
 * На дроті вони приходять як `2026-07-31 11:59:00.000` — пробіл замість `T` і жодної зони. Тому
 * `new Date(raw)` читає їх як локальний час і тихо зсуває на офсет глядача.
 *
 * **Читати їх треба по-різному, і це не примха, а різне походження значення:**
 *
 * - **Серверні колонки** (`created_at`, `updated_at`, `confirmed_at`, `completed_at`…) пише `now()`
 *   на UTC-сесії. Настінний час у них — це UTC, тож щоб показати київський, треба конвертувати:
 *   `parseNaiveTimestamp`.
 * - **Колонки, які обирає оператор** (`invoice_date`, `revaluation_date`, `return_date`,
 *   `occurred_at`) несуть настінний час, який оператор і бачив у календарі. Конвертувати його
 *   нікуди не треба — і не можна: `parseNaiveDateTime` / `naiveDatePart`.
 *
 * Застосувати не той хелпер — це зсув на 3 години, а на date-only полях після 21:00 ще й зсув
 * доби. Обидва мовчазні.
 */

const ZONE_SUFFIX = /[zZ]$|[+-]\d{2}:?\d{2}$/;

const NAIVE_WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/;

/** Настінний час, розкладений на компоненти. `hour`/`minute` = `null`, якщо значення без часу. */
export interface NaiveWallClock {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number | null;
  readonly minute: number | null;
}

/**
 * Позначка часу СЕРВЕРНОЇ колонки → мілісекунди епохи.
 *
 * Настінний час читається як UTC, бо саме ним його й записав `now()`. Значення, що вже несуть
 * зону, лишаються як є.
 *
 * Повертає `NaN` на будь-чому непарсабельному — точно як `Date.parse`, тож **компаратори
 * сортування мусять це перевіряти**, інакше один битий рядок перемішає весь порядок.
 */
export function parseNaiveTimestamp(value: string): number {
  const hasZone = ZONE_SUFFIX.test(value);
  return new Date(hasZone ? value : `${value.replace(' ', 'T')}Z`).getTime();
}

/**
 * `Date` → настінний час для ТІЛА запиту: `2026-08-01T14:30:00`.
 *
 * Бере локальні компоненти, а не UTC. `toISOString()` тут писати не можна двічі:
 *
 * 1. він додає `Z`, а `naiveTimestampSchema` таке відхиляє — PostgreSQL при касті в
 *    `timestamp without time zone` суфікс зони **відкидає**, а не конвертує, тож обраний час
 *    ліг би в базу на 3 години раніше;
 * 2. на date-only полі локальна північ у UTC — це 21:00 **попередньої** доби, тобто зсув
 *    цілого дня.
 *
 * Секунди лишаємо, мілісекунди — ні: календар їх не дає, а в базі вони тільки шумлять.
 */
export function toNaiveDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  const seconds = `${date.getSeconds()}`.padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

/**
 * Позначка часу колонки ОПЕРАТОРА → `Date` для календаря.
 *
 * Настінний час читається як локальний — саме таким його й обрали. Парність із
 * `toNaiveDateTime` тут головна: список і форма мусять показувати на одному рядку те саме
 * значення, інакше екран суперечить сам собі.
 *
 * На непарсабельному значенні повертає «зараз»: календар не має чим показати порожнечу, і
 * форма з `Validators.required` однаково не дасть зберегти сміття.
 */
export function parseNaiveDateTime(value: string): Date {
  const raw = ZONE_SUFFIX.test(value) ? value : value.replace(' ', 'T');
  const parsed = new Date(raw);
  return isNaN(parsed.getTime()) ? new Date() : parsed;
}

/**
 * Збережений настінний час → компоненти, без жодної конверсії зон:
 * `2026-04-22 21:39:38.691` → `{ year: 2026, month: 4, day: 22, hour: 21, minute: 39 }`.
 *
 * **Єдиний розклад наївного рядка в застосунку.** Ним користуються і показ
 * (`UkrDateTimeComponent`), і читання у календар (`parseNaiveDate`) — саме тому, що вони
 * зобовʼязані дати на одному рядку ту саму дату. Дві копії регекса розійшлися б.
 *
 * Конвертувати тут нічого: на полі, яке заповнює оператор, настінний час і є відповіддю. Будь-який
 * перерахунок його не уточнює, а псує — під UTC+3 рядок після 21:00 поїхав би наступною добою (на
 * dev таких 6 із 58 актів переоцінки і 4 з 39 прибуткових накладних, тобто це не край, а десята
 * частина даних).
 *
 * `null`, якщо значення несе зону або не починається з дати: тоді це вже момент, і викликач мусить
 * конвертувати його сам.
 */
export function naiveWallClock(value: string): NaiveWallClock | null {
  if (ZONE_SUFFIX.test(value)) return null;

  const parts = NAIVE_WALL_CLOCK.exec(value.trim());
  if (!parts) return null;

  const [, year, month, day, hour, minute] = parts;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: hour === undefined ? null : Number(hour),
    minute: minute === undefined ? null : Number(minute),
  };
}

/**
 * Позначка часу date-only колонки → `Date` для календаря, поставлений на ЛОКАЛЬНУ північ.
 *
 * Парний до `naiveWallClock`: показ бере дату з рядка, календар мусить показати ту саму. Через
 * `new Date(value)` цього не досягти — `new Date('2026-08-01')` за специфікацією UTC-північ, тож
 * у будь-якій відʼємній зоні календар відкрив би попередню добу, поки список показує правильну.
 * Київ завжди UTC+2/+3, тому сьогодні це не видно; тут воно й лишається невидимим.
 *
 * На непарсабельному значенні — «зараз», з тієї ж причини, що й у `parseNaiveDateTime`.
 */
export function parseNaiveDate(value: string): Date {
  const wall = naiveWallClock(value);
  return wall ? new Date(wall.year, wall.month - 1, wall.day) : parseNaiveDateTime(value);
}
